import { Banner, Button, Tag } from '@dev-spendesk/grapes';
import { useEffect, useState } from 'react';

import { useFeature } from 'common/hooks/useFeature';
import { useTranslation } from 'common/hooks/useTranslation';
import { useCompany } from 'modules/app/hooks/useCompany';
import { useUserRoles } from 'modules/app/hooks/useUserRoles';
import { useFetchKycRemediation } from 'modules/invoices/hooks/useFetchKycRemediation';
import { isEeaMember } from 'src/core/config/country';
import FEATURES from 'src/core/constants/features';
import { LocalStorageKey } from 'src/core/constants/storage';
import { AnalyticEventName, track } from 'src/core/utils/analytics';

export const InternationalWireTransferAccountValidationBanner = () => {
  const { t } = useTranslation('global');
  const company = useCompany();
  const { isAccountOwner } = useUserRoles();

  const [isOpen, setIsOpen] = useState(false);
  const isEEAOrEUROCurrency =
    company.currency === 'EUR' || isEeaMember(company.country);
  const isEeaInternationalTransfersEnabled = useFeature(
    FEATURES.TMP_EEA_INTERNATIONAL_WIRE_TRANSFER,
  );
  const hasBeenClosed = localStorage.getItem(
    LocalStorageKey.IwtRemediateBannerClosed,
  );
  const fetchKycRemediationResult = useFetchKycRemediation({
    isEnabled:
      isEeaInternationalTransfersEnabled &&
      isEEAOrEUROCurrency &&
      !hasBeenClosed,
  });

  useEffect(() => {
    if (
      fetchKycRemediationResult.status === 'success' &&
      fetchKycRemediationResult.data.status === 'draft'
    ) {
      setIsOpen(true);
    }
  }, [fetchKycRemediationResult]);

  const closeBanner = () => {
    setIsOpen(false);
    localStorage.setItem(LocalStorageKey.IwtRemediateBannerClosed, 'true');
  };

  const trackClose = () => {
    track(
      AnalyticEventName.SETTINGS_PAYMENT_METHODS_IWT_KYC_REMEDIATION_SKIPPED,
      { source: isAccountOwner ? 'AO Banner' : 'Controller Banner' },
    );
  };

  const openKycAndCloseBanner = () => {
    // should always be true here because we don't show the banner if the status is not 'success'
    if (fetchKycRemediationResult.status === 'success') {
      window.open(fetchKycRemediationResult.data.onboardingUrl, '_blank');
      track(
        AnalyticEventName.SETTINGS_PAYMENT_METHODS_IWT_KYC_REMEDIATION_OPENED,
        { source: 'AO Banner' },
      );
    }
    closeBanner();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Banner
      variant="brand"
      title={
        <div className="flex items-center gap-8">
          <h3 className="title-m">
            {isAccountOwner
              ? t(
                  'international-wiretransfer.accountValidationBanner.accountOwner.title',
                )
              : t(
                  'international-wiretransfer.accountValidationBanner.others.title',
                )}
          </h3>
          <Tag variant="pink">{t('misc.new')}</Tag>
        </div>
      }
      actions={
        isAccountOwner ? (
          <div className="flex gap-16">
            <Button
              className="pl-0"
              variant="tertiaryBrand"
              text={t(
                'international-wiretransfer.accountValidationBanner.accountOwner.notInterested',
              )}
              onClick={() => {
                closeBanner();
                trackClose();
              }}
            />
            <Button
              variant="primaryBrand"
              text={t(
                'international-wiretransfer.accountValidationModal.reviewCta',
              )}
              onClick={openKycAndCloseBanner}
            />
          </div>
        ) : (
          <Button
            iconName="cross"
            variant="secondaryNeutral"
            text={t('misc.close')}
            onClick={() => {
              closeBanner();
              trackClose();
            }}
          />
        )
      }
    >
      {isAccountOwner ? (
        t(
          'international-wiretransfer.accountValidationBanner.accountOwner.description',
        )
      ) : (
        <>
          <p>
            {t(
              'international-wiretransfer.accountValidationBanner.others.description',
            )}
          </p>
          <p>
            {t(
              'international-wiretransfer.accountValidationBanner.others.contactAO',
            )}
          </p>
        </>
      )}
    </Banner>
  );
};
